/**
 * Shared helpers for Diagnostic Decision routing and condition eligibility.
 *
 * Why this file exists:
 * Historically, DD pages and Patient Overview each re-implemented their own
 * checks for "does this patient have other conditions to decide?". Small
 * differences in those checks can create production dead-ends where one page
 * routes to secondary diagnoses while another page believes there are none.
 *
 * Centralising these checks guarantees that:
 * 1) routing decisions are deterministic across entry points, and
 * 2) malformed Firestore values (null/non-array) fail safely.
 */

export const ADHD_CONDITION_LABEL = 'Attention Deficit Hyperactivity Disorder (ADHD)';

/** Sidebar / page title for the non-ADHD differentials step (TOC flat DD). */
export const TOC_DIFFERENTIALS_STEP_TITLE = 'Decision on Differentials';

/** Same step for non-TOC flat orgs (WLC, etc.). */
export const FLAT_OTHER_CONDITIONS_STEP_TITLE = 'Decision for Other Conditions';

/**
 * Page and nav label for the flat-model "other conditions" DD step.
 * TOC uses "Decision on Differentials"; other flat orgs keep the legacy name.
 */
export const getFlatOtherConditionsStepTitle = (isTOCWorkflow) =>
  isTOCWorkflow ? TOC_DIFFERENTIALS_STEP_TITLE : FLAT_OTHER_CONDITIONS_STEP_TITLE;

/** Default Final Formulation step title (TOC and non-SLaM multi-step DD). */
export const FINAL_FORMULATION_STEP_TITLE = 'Final Formulation';

/**
 * SLaM / 3 Borough label for the Final Formulation step.
 *
 * SLaM clinicians asked for clearer guidance that this text box is the right
 * place to record triangulation across sources (e.g. informant input, SSI vs
 * CI) — not just a copy-paste of emerging formulation notes. Display-only;
 * Firestore field names and report section keys stay as "Final Formulation".
 */
export const SLAM_FINAL_FORMULATION_STEP_TITLE =
  'Final Formulation (including Triangulation)';

/** Page / sidebar title for the Final Formulation step in multi-step DD. */
export const getFinalFormulationStepTitle = (isSLaMWorkflow = false) =>
  isSLaMWorkflow ? SLAM_FINAL_FORMULATION_STEP_TITLE : FINAL_FORMULATION_STEP_TITLE;

const toArray = (value) => (Array.isArray(value) ? value : []);

const getClinicalInterviewSection12 = (patientDataModel) =>
  patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[12] || {};

export const getSecondaryComorbidDiagnoses = (patientDataModel) => {
  const section12 = getClinicalInterviewSection12(patientDataModel);
  return toArray(section12.secondaryComorbidDiagnoses);
};

export const getDifferentialDiagnoses = (patientDataModel) => {
  const section12 = getClinicalInterviewSection12(patientDataModel);
  return toArray(section12.differentialDiagnoses);
};

/**
 * Returns the condition list that should be decided on the DD "other conditions"
 * step for the current workflow mode.
 */
export const getActionableOtherConditions = ({ patientDataModel, isFlatDifferentials }) => {
  if (isFlatDifferentials) {
    return getDifferentialDiagnoses(patientDataModel)
      .filter((condition) => condition !== ADHD_CONDITION_LABEL);
  }

  return getSecondaryComorbidDiagnoses(patientDataModel);
};

/**
 * Whether there are non-primary conditions that need decisions on the DD
 * "other conditions" step. When skipDifferentialsDecisions is true (TOC),
 * this always returns false — the step is removed from the wizard and
 * emerging differentials are shown as a read-only list on the Formulation
 * page instead.
 */
export const hasActionableOtherConditions = ({
  patientDataModel, isFlatDifferentials, skipDifferentialsDecisions = false,
}) =>
  skipDifferentialsDecisions
    ? false
    : getActionableOtherConditions({ patientDataModel, isFlatDifferentials }).length > 0;

/**
 * Whether the recorded ADHD diagnostic decision is a positive outcome
 * ("Fully met" or "Partially met").
 *
 * Used to decide whether the MDT Plan page (Well Life Clinic's final DD step,
 * capturing patient goals + To-discuss-in-PAD selections for the Semble MDT
 * Report) applies: a "Not met" outcome means no PAD is planned, so the page
 * is skipped and the DD completes on the existing pages.
 *
 * @param {Object|undefined} ddSection1 - The 4_diagnosticDecision.sections['1'] object
 * @returns {boolean}
 */
export const isAdhdPositiveDecision = (ddSection1) =>
  ['Fully met', 'Partially met'].includes(ddSection1?.diagnosisDecision);

/**
 * Computes where the DD module should start from Patient Overview.
 *
 * We only jump straight to the secondary/other-conditions page when:
 * - the ADHD/primary DD section is already complete,
 * - the stage itself is not complete yet, and
 * - there are actionable non-primary conditions to decide.
 *
 * Otherwise we always start at the primary DD route.
 *
 * For multi-step DD patients (hasMultiStepDD — TOC, SLaM, etc.), the Review
 * pre-decision step is always the entry point while the stage is incomplete.
 * Once the stage is complete, they fall through to the standard flat /adhd
 * review path.
 *
 * For Semble MDT Report orgs (WLC), an ADHD-positive patient whose decision
 * pages are done but whose stage is still incomplete resumes at the MDT Plan
 * page — the deferred final step where the module now completes.  When other
 * conditions are still actionable, resume goes to the other-conditions page
 * first (its forward navigation leads on to the MDT Plan).
 */
export const getDiagnosticDecisionStartRoute = ({ patientId, patientDataModel, isFlatDifferentials, isDFDeferred = false, isTOCWorkflow = false, hasMultiStepDD = false, isSembleMdtReportEnabled = false, skipDifferentialsDecisions = false }) => {
  const ddStage = patientDataModel?.dataModel?.['4_diagnosticDecision'];
  const primarySectionDone = ddStage?.sections?.['1']?.status === 'completed';
  const stageComplete = ddStage?.status === 'completed';
  const hasOtherConditions = hasActionableOtherConditions({ patientDataModel, isFlatDifferentials, skipDifferentialsDecisions });

  // Multi-step DD orgs (TOC, SLaM, WLC) enter the DD module at the Review step
  // the FIRST time (stage still `notStarted`). Once the clinician has entered
  // the module (stage promoted to `inProgress`, or the ADHD section is already
  // done), "Continue" resumes them at the furthest incomplete step. When
  // reviewing a completed stage, they fall through to /adhd like any other org.
  // Backward compat: isTOCWorkflow still works for callers that haven't migrated.
  if ((hasMultiStepDD || isTOCWorkflow) && !stageComplete) {
    const ddEntered = ddStage?.status === 'inProgress' || primarySectionDone;
    if (!ddEntered) {
      return `/patient/${patientId}/proforma/diagnostic-decision/mdt-review`;
    }

    // When formulation is done and MDT Plan is the remaining step (WLC V2),
    // resume at the MDT Plan page rather than the ADHD decision page.
    const formulationDone = ddStage?.sections?.['3']?.status === 'completed';
    if (
      formulationDone &&
      isSembleMdtReportEnabled &&
      isAdhdPositiveDecision(ddStage?.sections?.['1'])
    ) {
      return `/patient/${patientId}/proforma/diagnostic-decision/mdt-plan`;
    }

    return `/patient/${patientId}/proforma/diagnostic-decision/adhd`;
  }

  // Deferred DF orgs always use 1_DD.js (/primary-diagnosis) even when flat,
  // because 1_DD_NO_RDD.js is dead code and not supported for deferred DF.
  const useFlatRoute = isFlatDifferentials && !isDFDeferred;

  if (primarySectionDone && !stageComplete && hasOtherConditions) {
    return `/patient/${patientId}/proforma/diagnostic-decision/${useFlatRoute ? 'other-conditions' : 'secondary-diagnoses'}`;
  }

  // Semble MDT Report orgs: primary decision recorded (ADHD-positive), no
  // other conditions pending, stage not yet complete — the only remaining
  // step is the MDT Plan page, so resume the clinician there.
  if (
    isSembleMdtReportEnabled &&
    primarySectionDone &&
    !stageComplete &&
    !hasOtherConditions &&
    isAdhdPositiveDecision(ddStage?.sections?.['1'])
  ) {
    return `/patient/${patientId}/proforma/diagnostic-decision/mdt-plan`;
  }

  return `/patient/${patientId}/proforma/diagnostic-decision/${useFlatRoute ? 'adhd' : 'primary-diagnosis'}`;
};

/**
 * Checks whether the ADHD diagnosis decision in Firestore is validly complete.
 *
 * This mirrors the isFormValid() logic in 1_DD.js but reads from persisted
 * Firestore fields (ddSection1 object) rather than local React selections state.
 * Each field is auto-saved to Firestore immediately when the clinician selects
 * it, so this gives an accurate picture of whether the form is "ready".
 *
 * Validation rules (matching the forward navigation button in 1_DD.js):
 *   - "Fully met": requires diagnosisPresentation + diagnosisSeverity
 *   - "Partially met": requires partialDiagnosisClassification; if that is
 *     "Other Specified ADHD", also requires otherSpecifiedReason; if that is
 *     "Other", also requires otherSpecifiedReasonText (non-empty)
 *   - "Not met": valid immediately (next steps required only in the dialog)
 *
 * @param {Object|undefined} ddSection1 - The 4_diagnosticDecision.sections['1'] object
 * @returns {boolean}
 */
export const isAdhdDecisionValid = (ddSection1) => {
  const decision = ddSection1?.diagnosisDecision;
  if (!decision) return false;

  if (decision === 'Fully met') {
    return Boolean(ddSection1.diagnosisPresentation) && Boolean(ddSection1.diagnosisSeverity);
  }

  if (decision === 'Partially met') {
    const partialType = ddSection1.partialDiagnosisClassification;
    if (!partialType) return false;

    if (partialType === 'Other Specified ADHD') {
      const reason = ddSection1.otherSpecifiedReason;
      if (!reason) return false;

      if (reason === 'Other') {
        const text = ddSection1.otherSpecifiedReasonText;
        return Boolean(text && text.trim());
      }
    }
    return true;
  }

  // "Not met" — valid immediately (next steps required only in the dialog,
  // not for the forward button / sidebar unlock)
  return true;
};

/**
 * Checks whether every "other condition" (non-ADHD differential / secondary
 * comorbid) has had a diagnostic decision recorded in Firestore.
 *
 * Why this exists:
 * The "Decision for Other Conditions" step (2_CSD.js) requires the clinician to
 * pick a decision for each condition before moving on. We need that same
 * completeness check away from the page's local React state — for the sidebar
 * green tick and to gate the Final Formulation step — so this reads directly
 * from the persisted `finalComorbidDiagnoses` map. It deliberately mirrors the
 * page's own `updateFormValidity` (every condition must have a truthy decision)
 * and the `isAdhdDecisionValid` pattern used for the ADHD step.
 *
 * Robustness notes:
 *   - We iterate the CURRENT actionable condition list (getActionableOtherConditions),
 *     so stale/removed entries left behind in Firestore neither block completion
 *     nor are required.
 *   - An empty actionable list returns `true` (there is nothing to decide), which
 *     is why callers gate this behind a `hasOther` check before it matters.
 *   - `notes` are intentionally NOT required — only a decision selection is.
 *
 * @param {Object} opts
 * @param {Object} opts.patientDataModel - Patient data from Firestore
 * @param {boolean} opts.isFlatDifferentials - Whether the org uses the flat DF model
 * @returns {boolean}
 */
export const isOtherConditionsComplete = ({ patientDataModel, isFlatDifferentials }) => {
  const conditions = getActionableOtherConditions({ patientDataModel, isFlatDifferentials });
  if (conditions.length === 0) return true;

  const decided = patientDataModel?.dataModel?.['4_diagnosticDecision']
    ?.sections?.['2']?.finalComorbidDiagnoses || {};

  return conditions.every((condition) => Boolean(decided[condition]?.decision));
};

/**
 * Computes the Diagnostic Decision module progress percentage for the Patient
 * Overview card.
 *
 * Two models:
 *  - Non-multi-step orgs (Well Life Clinic, most): unchanged coarse heuristic —
 *    100 when the stage is complete, 50 when the ADHD section is done, else the
 *    ADHD section's own progress (or 0).
 *  - Multi-step orgs (TOC, SLaM, 3 Borough): step-based fixed anchors so the
 *    card reflects real movement through the wizard and reads "Continue" (not
 *    "Start") as soon as the module has been entered:
 *      0   -> not entered
 *      25  -> entered (Review visited; stage promoted to 'inProgress')
 *      50  -> ADHD decision confirmed (sections['1'] complete)
 *      75  -> Other Conditions decided (only when there are actionable ones)
 *      100 -> stage complete (Final Formulation finished)
 *    When there are no Other Conditions the 75 rung is skipped (50 -> 100), by
 *    deliberate product choice (we do not re-split into 33/67/100).
 *
 * "Entered" means stage status === 'inProgress'. We also treat a completed ADHD
 * section as entered, so legacy in-flight patients (whose stage status was never
 * promoted before this feature shipped) never regress below their real 50/75.
 *
 * @param {Object} opts
 * @param {Object} opts.patientDataModel
 * @param {boolean} opts.isFlatDifferentials
 * @param {boolean} [opts.hasMultiStepDD]
 * @param {boolean} [opts.isTOCWorkflow]
 * @param {boolean} [opts.isSembleMdtReportEnabled] - When true and ADHD is
 *   positive, an extra MDT Plan step exists between Formulation and completion.
 *   Progress milestones shift to accommodate the 5th step.
 * @returns {number} 0-100
 */
export const computeDDModuleProgress = ({
  patientDataModel,
  isFlatDifferentials,
  hasMultiStepDD = false,
  isTOCWorkflow = false,
  isSembleMdtReportEnabled = false,
  skipDifferentialsDecisions = false,
}) => {
  const ddStage = patientDataModel?.dataModel?.['4_diagnosticDecision'];
  if (ddStage?.status === 'completed') return 100;

  const section1 = ddStage?.sections?.['1'];
  const section1Complete = section1?.status === 'completed';

  const isMultiStep = hasMultiStepDD || isTOCWorkflow;
  if (!isMultiStep) {
    return section1Complete ? 50 : (section1?.progress || 0);
  }

  if (section1Complete) {
    const hasOther = hasActionableOtherConditions({ patientDataModel, isFlatDifferentials, skipDifferentialsDecisions });
    const otherComplete = isOtherConditionsComplete({ patientDataModel, isFlatDifferentials });

    // When MDT Plan is the 5th step (Semble MDT orgs with a positive ADHD
    // outcome), the wizard has one more rung: Formulation complete but MDT
    // Plan still pending.  Progress milestones: 20 -> 40 -> 60 -> 80 -> 100.
    // Without MDT Plan the original 25 -> 50 -> 75 -> 100 model applies.
    const hasMdtPlanStep = isSembleMdtReportEnabled
      && isAdhdPositiveDecision(section1);

    if (hasMdtPlanStep) {
      // 5-step model: Review(20) -> ADHD(40) -> [Others(60)] -> Formulation(80) -> MDT Plan(100)
      const section3 = ddStage?.sections?.['3'];
      const formulationComplete = section3?.status === 'completed' && Boolean(section3?.finalFormulation);
      if (formulationComplete) return 80;
      if (hasOther && otherComplete) return 60;
      return 40;
    }

    // 4-step model (no MDT Plan): Review(25) -> ADHD(50) -> [Others(75)] -> Formulation/complete(100)
    return hasOther && otherComplete ? 75 : 50;
  }

  // Not yet decided ADHD: 25% once the module has been entered, else 0%.
  return ddStage?.status === 'inProgress' ? 25 : 0;
};

/**
 * Absolute routes for each multi-step ADHD DD page (TOC / SLaM).
 *
 * Kept as a named helper so sidebar index→route mapping and any future
 * deep-links share one path construction — avoid hard-coding the four URLs
 * in each page component.
 *
 * @param {string} patientId
 * @returns {{ mdtReview: string, adhd: string, otherConditions: string, finalFormulation: string }}
 */
export const getDDStepRoutes = (patientId) => ({
  mdtReview: `/patient/${patientId}/proforma/diagnostic-decision/mdt-review`,
  adhd: `/patient/${patientId}/proforma/diagnostic-decision/adhd`,
  otherConditions: `/patient/${patientId}/proforma/diagnostic-decision/other-conditions`,
  finalFormulation: `/patient/${patientId}/proforma/diagnostic-decision/final-formulation`,
  mdtPlan: `/patient/${patientId}/proforma/diagnostic-decision/mdt-plan`,
});

/**
 * Maps a DDSideNavbar section index to its route, accounting for whether the
 * "Decision for Other Conditions" step is present in the sidebar.
 *
 * Why this exists (bug history):
 *   `buildDDNavSections` omits Other Conditions when there are no actionable
 *   differentials, so Final Formulation becomes index 2. Page components used
 *   to keep a hard-coded 4-route array, so clicking Final Formulation navigated
 *   to `/other-conditions`. That page immediately redirects back to ADHD when
 *   empty — which looked like "Final Formulation reloads Decision for ADHD".
 *
 * Mirror of `getAutismDDRouteForSectionIndex` for the ADHD DD module.
 *
 * @param {string} patientId
 * @param {number} index - zero-based sidebar index from DDSideNavbar
 * @param {boolean} hasOther - whether the Other Conditions step is in the sidebar
 * @param {boolean} [hasMdtPlan=false] - whether the MDT Plan step is in the sidebar
 * @returns {string} absolute route path
 */
export const getDDRouteForSectionIndex = (patientId, index, hasOther, hasMdtPlan = false) => {
  const routes = getDDStepRoutes(patientId);
  const ordered = [routes.mdtReview, routes.adhd];
  if (hasOther) ordered.push(routes.otherConditions);
  ordered.push(routes.finalFormulation);
  if (hasMdtPlan) ordered.push(routes.mdtPlan);
  return ordered[index] || routes.mdtReview;
};

/**
 * Builds the DDSideNavbar sections array for multi-step DD patients (TOC, SLaM, WLC).
 *
 * The DD module has up to five steps:
 *   0. Review             — always present (titled "MDT Review" for MDT orgs, "Review" for others)
 *   1. Decision for ADHD  — always present
 *   2. Decision for Other Conditions — only if non-ADHD differentials exist
 *   3. Formulation        — always present
 *   4. MDT Plan           — only for Semble MDT Report orgs (WLC) with an ADHD-positive decision
 *
 * When there are no other conditions the sidebar has three or four items
 * (without/with MDT Plan). When other conditions exist, one more.
 *
 * Centralising the construction here prevents the sections array from drifting
 * across page components. Sidebar clicks must use `getDDRouteForSectionIndex`
 * with matching `hasOther` and `hasMdtPlan` so indices stay aligned.
 *
 * @param {Object} opts
 * @param {Object} opts.patientDataModel - Patient data from Firestore
 * @param {boolean} opts.isFlatDifferentials - Whether the org uses flat DF model
 * @param {number} opts.currentSectionIndex - Zero-based active page index
 * @param {boolean} [opts.isTOCWorkflow=true] - Whether this is a TOC patient (affects "Decision on Differentials" title)
 * @param {boolean} [opts.isSLaMWorkflow=false] - SLaM / 3 Borough (longer Final Formulation title)
 * @param {boolean} [opts.isMDTReview=false] - Whether the DD uses MDT Review style (controls "MDT Review" vs "Review" title)
 * @param {boolean} [opts.isSembleMdtReportEnabled=false] - Whether this patient has the Semble MDT Report / MDT Plan feature
 */
export const buildDDNavSections = ({
  patientDataModel,
  isFlatDifferentials,
  currentSectionIndex,
  isTOCWorkflow = true,
  isSLaMWorkflow = false,
  isMDTReview = false,
  isSembleMdtReportEnabled = false,
  skipDifferentialsDecisions = false,
}) => {
  const ddSection1       = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['1'];
  const ddSection1Status = ddSection1?.status;
  const ddSection3       = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['3'];
  const ddStageStatus    = patientDataModel?.dataModel?.['4_diagnosticDecision']?.status;
  const hasOther = hasActionableOtherConditions({ patientDataModel, isFlatDifferentials, skipDifferentialsDecisions });

  // A section is only considered truly complete if the status flag is set AND
  // the clinician actually recorded a diagnosisDecision value.  This prevents
  // the green tick from appearing on stale/test data where the status was set
  // but the decision content was cleared or never fully committed.
  const isSection1Complete = ddSection1Status === 'completed' && Boolean(ddSection1?.diagnosisDecision);

  // Check whether the ADHD diagnosis form has valid values (all required
  // follow-up fields filled). This mirrors the isFormValid() logic in 1_DD.js
  // but reads from persisted Firestore data rather than local React state.
  // Each sub-field is auto-saved to Firestore immediately on selection, so
  // this check accurately reflects what's been committed. This allows
  // downstream sidebar items (Final Formulation, Other Conditions) to be
  // navigable as soon as the form is validly filled — critical for supporting
  // clinicians who cannot click the Confirm button (read-only) but still need
  // sidebar access to view Formulation.
  const hasDecisionBeenMade = isAdhdDecisionValid(ddSection1);

  // Whether every "other condition" has a recorded decision. This drives both
  // the Other Conditions green tick and the Final Formulation lock below, so a
  // clinician cannot reach/complete the formulation ("bringing it all together")
  // while any differential is still undecided.
  const otherComplete = isOtherConditionsComplete({ patientDataModel, isFlatDifferentials });

  // A fully completed DD stage must always stay navigable when revisited, even
  // if the actionable condition list has since changed (e.g. a differential was
  // added to the DF after completion). This override prevents such edge/legacy
  // data from re-locking a finished module or dropping the green tick.
  const stageComplete = ddStageStatus === 'completed';

  // Derive titles based on workflow flags. MDT Review orgs (TOC, WLC, etc.)
  // show "MDT Review"; non-MDT orgs (SLaM) show "Review". The other-conditions
  // step title is TOC-specific ("Decision on Differentials") vs the default
  // ("Decision for Other Conditions") used by WLC, SLaM, and everyone else.
  const reviewTitle = (isMDTReview || isTOCWorkflow) ? 'MDT Review' : 'Review';
  const otherConditionsTitle = isTOCWorkflow
    ? TOC_DIFFERENTIALS_STEP_TITLE
    : FLAT_OTHER_CONDITIONS_STEP_TITLE;

  const sections = [
    {
      sectionTitle: reviewTitle,
      isUnlocked: true,
      // Once the clinician has moved past the Review page (i.e. they are
      // now on the ADHD Decision or Other Conditions page), Review is
      // considered complete.  This avoids needing a dedicated Firestore flag.
      status: currentSectionIndex > 0 ? 'completed' : 'notStarted',
    },
    {
      sectionTitle: 'Decision for ADHD',
      isUnlocked: true,
      status: isSection1Complete ? 'completed' : 'notStarted',
    },
  ];

  // Conditionally include the differentials step only when non-ADHD
  // differentials exist.  When absent, Formulation slots in directly after ADHD.
  if (hasOther) {
    sections.push({
      sectionTitle: otherConditionsTitle,
      isUnlocked: hasDecisionBeenMade,
      // Green-tick as soon as every differential has a decision (matching the
      // ADHD step and Autism DD), not only when the whole stage is complete.
      // `stageComplete` is kept as a safety net so a finished module never loses
      // its tick on stale/legacy data.
      status: otherComplete || stageComplete ? 'completed' : 'notStarted',
    });
  }

  // Formulation unlocks once the ADHD decision is recorded AND (when other
  // conditions exist) all of those have been decided — the formulation "brings
  // it all together", so we don't let clinicians reach it with undecided
  // differentials. A completed stage stays navigable via the `stageComplete`
  // override. When there are no other conditions it unlocks from the ADHD
  // decision alone, exactly as before.
  const isFormulationComplete = ddSection3?.status === 'completed' && Boolean(ddSection3?.finalFormulation);
  sections.push({
    sectionTitle: getFinalFormulationStepTitle(isSLaMWorkflow),
    isUnlocked: hasDecisionBeenMade && (!hasOther || otherComplete || stageComplete),
    status: isFormulationComplete ? 'completed' : 'notStarted',
  });

  // MDT Plan — the final step for Semble MDT Report orgs (WLC). Only shown
  // when the feature is enabled AND the ADHD decision is positive ("Fully met"
  // or "Partially met"). A "Not met" outcome means no PAD is planned, so the
  // MDT Plan page (which captures goals and items to discuss in the PAD) is
  // skipped and the DD completes on Formulation as usual.
  const adhdPositive = isAdhdPositiveDecision(ddSection1);
  if (isSembleMdtReportEnabled && adhdPositive) {
    const mdtPlanData = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['4'];
    const isMdtPlanComplete = mdtPlanData?.status === 'completed';
    sections.push({
      sectionTitle: 'MDT Plan',
      isUnlocked: isFormulationComplete || stageComplete,
      status: isMdtPlanComplete || stageComplete ? 'completed' : 'notStarted',
    });
  }

  return sections;
};
